<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-gray-900">إدارة اشتراكات الطلاب</h2>
        <button @click="loadData" class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">تحديث</button>
      </div>

      <div v-if="errorMessage" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700">
        {{ errorMessage }}
      </div>

      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">تفعيل اشتراك للطالب</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="space-y-2">
            <input
              v-model.trim="studentSearch"
              type="text"
              class="w-full p-2 border border-gray-300 rounded-md"
              placeholder="ابحث عن الطالب بالاسم أو الرقم"
            />
            <select v-model="form.student_id" class="w-full p-2 border border-gray-300 rounded-md">
              <option value="">اختر الطالب</option>
              <option v-for="student in students" :key="student.id" :value="student.id">
                {{ student.full_name || `#${student.id}` }}
              </option>
            </select>
          </div>
          <select v-model="form.package_id" class="w-full p-2 border border-gray-300 rounded-md">
            <option value="">اختر الباقة</option>
            <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
              {{ pkg.name }} - {{ pkg.is_free ? 'مجاني' : `${Number(pkg.price || 0).toFixed(2)} ${pkg.currency || 'SAR'}` }}
            </option>
          </select>
          <input v-model="form.start_date" type="date" class="w-full p-2 border border-gray-300 rounded-md" />
          <select v-model="form.payment_method" class="w-full p-2 border border-gray-300 rounded-md">
            <option value="manual">يدوي</option>
            <option value="free">مجاني</option>
            <option value="urway">URWAY</option>
          </select>
        </div>
        <div class="mt-3 flex items-center gap-2">
          <button @click="activateSubscription" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md">تفعيل اشتراك</button>
          <button @click="createPayment" class="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md">إنشاء دفع URWAY</button>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow overflow-x-auto">
        <table class="min-w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الطالب</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الباقة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الحالة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">من</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">إلى</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">إجراءات</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="sub in subscriptions" :key="sub.id">
              <td class="px-4 py-3 text-sm text-center">{{ sub.student_name }}</td>
              <td class="px-4 py-3 text-sm text-center">{{ sub.package_name || sub.subscription_name }}</td>
              <td class="px-4 py-3 text-sm text-center">
                <select :value="sub.status" @change="updateStatus(sub, $event.target.value)" class="p-1 border border-gray-300 rounded-md text-sm">
                  <option value="active">نشط</option>
                  <option value="expired">منتهي</option>
                  <option value="suspended">معلق</option>
                  <option value="cancelled">ملغي</option>
                </select>
              </td>
              <td class="px-4 py-3 text-sm text-center">{{ sub.start_date }}</td>
              <td class="px-4 py-3 text-sm text-center">{{ sub.end_date }}</td>
              <td class="px-4 py-3 text-sm text-center">
                <button @click="expireNow(sub)" class="bg-red-50 text-red-700 hover:bg-red-100 px-3 py-1 rounded">إنهاء الآن</button>
              </td>
            </tr>
            <tr v-if="!subscriptions.length">
              <td colspan="6" class="px-4 py-6 text-center text-gray-500">لا توجد اشتراكات حالياً</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import {
  createIqraStudentSubscription,
  createIqraSubscriptionPaymentIntent,
  listIqraStudentSubscriptions,
  listIqraStudents,
  listIqraSubscriptionPackages,
  updateIqraStudentSubscription,
} from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraStudentSubscriptionsManagement',
  data() {
    return {
      loading: false,
      errorMessage: '',
      studentSearch: '',
      studentSearchTimer: null,
      studentsLoading: false,
      studentsRequestKey: 0,
      students: [],
      packages: [],
      subscriptions: [],
      form: {
        student_id: '',
        package_id: '',
        start_date: new Date().toISOString().slice(0, 10),
        payment_method: 'manual',
      },
    };
  },
  watch: {
    studentSearch() {
      if (this.studentSearchTimer) {
        clearTimeout(this.studentSearchTimer);
      }
      this.studentSearchTimer = setTimeout(() => {
        this.fetchStudents().catch(() => {});
      }, 300);
    },
  },
  async mounted() {
    await this.loadData();
  },
  beforeUnmount() {
    if (this.studentSearchTimer) {
      clearTimeout(this.studentSearchTimer);
    }
  },
  methods: {
    async fetchStudents() {
      this.studentsLoading = true;
      const requestKey = ++this.studentsRequestKey;
      try {
        const query = (this.studentSearch || '').trim();
        const params = {
          page: 1,
          size: query ? 50 : 200,
          ...(query ? { q: query } : {}),
        };
        const studentsResult = await listIqraStudents(params);
        if (requestKey !== this.studentsRequestKey) return;
        this.students = studentsResult?.data || [];
      } catch (error) {
        if (requestKey !== this.studentsRequestKey) return;
        this.errorMessage = error?.message || 'فشل في تحميل قائمة طلاب';
      } finally {
        if (requestKey === this.studentsRequestKey) {
          this.studentsLoading = false;
        }
      }
    },
    async loadData() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const [, packagesResult, subscriptionsResult] = await Promise.all([
          this.fetchStudents(),
          listIqraSubscriptionPackages({ page: 1, size: 200 }),
          listIqraStudentSubscriptions({ page: 1, size: 200 }),
        ]);
        this.packages = packagesResult?.data || [];
        this.subscriptions = subscriptionsResult?.data || [];
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل البيانات';
      } finally {
        this.loading = false;
      }
    },
    async activateSubscription() {
      if (!this.form.student_id || !this.form.package_id) {
        alert('يرجى اختيار الطالب والباقة');
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      try {
        await createIqraStudentSubscription({
          student_id: this.form.student_id,
          package_id: this.form.package_id,
          start_date: this.form.start_date || undefined,
          payment_method: this.form.payment_method,
        });
        await this.loadData();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تفعيل الاشتراك';
      } finally {
        this.loading = false;
      }
    },
    async createPayment() {
      if (!this.form.student_id || !this.form.package_id) {
        alert('يرجى اختيار الطالب والباقة');
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      try {
        const payment = await createIqraSubscriptionPaymentIntent({
          student_id: this.form.student_id,
          package_id: this.form.package_id,
        });
        const url = payment?.redirect_url;
        if (url) {
          window.open(url, '_blank', 'noopener');
        }
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في إنشاء عملية الدفع';
      } finally {
        this.loading = false;
      }
    },
    async updateStatus(sub, status) {
      this.loading = true;
      this.errorMessage = '';
      try {
        await updateIqraStudentSubscription(sub.id, { status });
        await this.loadData();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحديث الحالة';
      } finally {
        this.loading = false;
      }
    },
    async expireNow(sub) {
      this.loading = true;
      this.errorMessage = '';
      try {
        await updateIqraStudentSubscription(sub.id, {
          status: 'expired',
          end_date: new Date().toISOString().slice(0, 10),
        });
        await this.loadData();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في إنهاء الاشتراك';
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>
